/**
 * Controlled JSON rich-text editor for clinical note and reply composition.
 *
 * Formatting is applied to the structured document model rather than through
 * deprecated browser editing commands.
 */

import type { RichTextDocument, RichTextMark } from "../../../types/notes";
import {
  plainTextToRichText,
  richTextToPlainText,
  toggleDocumentMark,
} from "../../../utils/richText";

interface RichTextEditorProps {
  value: RichTextDocument;
  onChange: (value: RichTextDocument) => void;
  placeholder: string;
  minRows?: number;
  ariaLabel: string;
}

const MARKS: Array<{ mark: RichTextMark; label: string; title: string }> = [
  { mark: "bold", label: "B", title: "Bold" },
  { mark: "italic", label: "I", title: "Italic" },
  { mark: "underline", label: "U", title: "Underline" },
];

/**
 * Render a plain-text input backed by the safe structured rich-text contract.
 *
 * @param props - Controlled document, change callback, and accessibility labels
 * @returns Rich-text composition controls
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minRows = 3,
  ariaLabel,
}: RichTextEditorProps) {
  const activeMarks = value.content.blocks[0]?.content[0]?.marks ?? [];
  return (
    <div className="overflow-hidden rounded-md border border-uiGray-300 bg-white">
      <div className="flex gap-1 border-b border-uiGray-200 bg-uiGray-150 px-2 py-1">
        {MARKS.map(({ mark, label, title }) => (
          <button
            key={mark}
            type="button"
            title={title}
            aria-pressed={activeMarks.includes(mark)}
            onClick={() => onChange(toggleDocumentMark(value, mark))}
            className={`h-7 w-7 rounded text-sm ${
              activeMarks.includes(mark) ? "bg-statusBlue-bg text-statusBlue-darkText" : "hover:bg-uiGray-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <textarea
        aria-label={ariaLabel}
        rows={minRows}
        value={richTextToPlainText(value)}
        placeholder={placeholder}
        onChange={(event) =>
          onChange(plainTextToRichText(event.target.value, activeMarks))
        }
        className="w-full resize-y bg-white px-3 py-2 text-sm leading-6 outline-none"
      />
    </div>
  );
}
